import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import dayjs from 'dayjs';
import SidebarLayout from './SidebarLayout';
import { Bar, Line, Doughnut } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://api.ogestyle.com';

const ProductAnalytics = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState('30days');
  const [selectedLocation, setSelectedLocation] = useState('all');
  const [locations, setLocations] = useState([]);
  
  // Data states
  const [salesData, setSalesData] = useState(null);
  const [performanceData, setPerformanceData] = useState(null);
  const [inventoryReport, setInventoryReport] = useState(null);
  const [categoryData, setCategoryData] = useState(null);

  useEffect(() => {
    fetchLocations();
  }, []);

  useEffect(() => {
    fetchAnalyticsData();

    // Auto-refresh every 30 seconds
    const interval = setInterval(() => {
      fetchAnalyticsData();
    }, 30000);

    // Cleanup interval on unmount or when dateRange or location changes
    return () => clearInterval(interval);
  }, [dateRange, selectedLocation]);

  const fetchLocations = async () => {
    const token = localStorage.getItem('ogestyle_token');
    try {
      const response = await axios.get(`${API_BASE_URL}/api/shop/my-shops`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Response is either an array or { shops: [] }
      const shops = Array.isArray(response.data) ? response.data : (response.data.shops || []);
      setLocations(shops);
    } catch (error) {
    }
  };

  const getDateRange = () => {
    const end = dayjs();
    let start;

    switch (dateRange) {
      case '7days':
        start = dayjs().subtract(7, 'days');
        break;
      case '30days':
        start = dayjs().subtract(30, 'days');
        break;
      case '90days':
        start = dayjs().subtract(90, 'days');
        break;
      case 'year':
        start = dayjs().subtract(1, 'year');
        break;
      case 'all':
        start = dayjs().subtract(10, 'years');
        break;
      default:
        start = dayjs().subtract(30, 'days');
    }

    return {
      start: start.format('YYYY-MM-DD'),
      end: end.format('YYYY-MM-DD')
    };
  };

  const fetchAnalyticsData = async () => {
    setLoading(true);
    const token = localStorage.getItem('ogestyle_token');
    const { start, end } = getDateRange();
    const shopIdParam = selectedLocation !== 'all' ? `&shopId=${selectedLocation}` : '';

    try {
      const [sales, performance, inventory, categories] = await Promise.all([
        axios.get(`${API_BASE_URL}/api/analytics/product-sales?start=${start}&end=${end}${shopIdParam}`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API_BASE_URL}/api/analytics/product-performance?start=${start}&end=${end}${shopIdParam}`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API_BASE_URL}/api/analytics/inventory-report?start=${start}&end=${end}${shopIdParam}`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API_BASE_URL}/api/analytics/category-performance?start=${start}&end=${end}${shopIdParam}`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);

      setSalesData(sales.data);
      setPerformanceData(performance.data);
      setInventoryReport(inventory.data);
      setCategoryData(categories.data);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  // Chart: Top Selling Products (Bar Chart)
  const topProductsChartData = () => {
    if (!salesData?.sales) return null;

    const top10 = salesData.sales.slice(0, 10);

    return {
      labels: top10.map(p => p.name),
      datasets: [{
        label: 'Revenue ($)',
        data: top10.map(p => p.revenue),
        backgroundColor: 'rgba(59, 130, 246, 0.8)',
        borderColor: 'rgb(59, 130, 246)',
        borderWidth: 1
      }]
    };
  };

  // Chart: Category Revenue (Doughnut)
  const categoryRevenueChartData = () => {
    if (!categoryData?.categories) return null;

    const colors = [
      'rgba(59, 130, 246, 0.8)',
      'rgba(16, 185, 129, 0.8)',
      'rgba(245, 158, 11, 0.8)',
      'rgba(239, 68, 68, 0.8)',
      'rgba(139, 92, 246, 0.8)',
      'rgba(236, 72, 153, 0.8)'
    ];

    return {
      labels: categoryData.categories.map(c => c.category),
      datasets: [{
        data: categoryData.categories.map(c => c.revenue),
        backgroundColor: colors,
        borderColor: colors.map(c => c.replace('0.8', '1')),
        borderWidth: 2
      }]
    };
  };

  // Chart: Stock Velocity (Bar Chart)
  const velocityChartData = () => {
    if (!performanceData?.performance) return null;

    // Top 10 by sales velocity
    const sorted = [...performanceData.performance]
      .sort((a, b) => parseFloat(b.salesVelocity) - parseFloat(a.salesVelocity))
      .slice(0, 10);

    return {
      labels: sorted.map(p => p.name),
      datasets: [{
        label: 'Sales Velocity (units/day)',
        data: sorted.map(p => parseFloat(p.salesVelocity)),
        backgroundColor: 'rgba(16, 185, 129, 0.8)',
        borderColor: 'rgb(16, 185, 129)',
        borderWidth: 1
      }]
    };
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: true,
        position: 'top'
      },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        padding: 12,
        titleFont: { size: 14 },
        bodyFont: { size: 13 }
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        grid: {
          color: 'rgba(0, 0, 0, 0.05)'
        }
      },
      x: {
        grid: {
          display: false
        }
      }
    }
  };

  if (loading) {
    return (
      <SidebarLayout>
        <div className="analytics-page" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>📊</div>
            <div style={{ fontSize: '18px', color: '#666' }}>Loading Product Analytics...</div>
          </div>
        </div>
      </SidebarLayout>
    );
  }

  return (
    <SidebarLayout>
      <div className="analytics-page" style={{ padding: window.innerWidth <= 768 ? '16px' : '32px' }}>
        {/* Header */}
        <div style={{ 
          marginBottom: '32px',
          display: 'flex',
          flexDirection: window.innerWidth <= 768 ? 'column' : 'row',
          alignItems: window.innerWidth <= 768 ? 'flex-start' : 'center',
          justifyContent: 'space-between',
          gap: '16px'
        }}>
          <div>
            <h2 style={{ fontSize: window.innerWidth <= 768 ? '24px' : '32px', fontWeight: '700', margin: '0 0 8px 0' }}>
              📊 Product Analytics
            </h2>
            <p style={{ color: '#6b7280', margin: 0 }}>
              Sales velocity, inventory turnover, and performance metrics
            </p>
          </div>

          {/* Filters */}
          <div style={{ 
            display: 'flex', 
            gap: '12px',
            flexDirection: window.innerWidth <= 768 ? 'column' : 'row'
          }}>
            {/* Location Selector */}
            <select
              value={selectedLocation}
              onChange={(e) => setSelectedLocation(e.target.value)}
              style={{
                padding: '12px 16px',
                fontSize: '14px',
                fontWeight: '600',
                border: '2px solid #e5e7eb',
                borderRadius: '8px',
                background: 'white',
                cursor: 'pointer',
                outline: 'none',
                minWidth: '200px'
              }}
            >
              <option value="all">All Locations</option>
              {locations.map(loc => (
                <option key={loc._id} value={loc._id}>
                  {loc.name}
                </option>
              ))}
            </select>

            {/* Date Range Selector */}
            <select
              value={dateRange}
              onChange={(e) => setDateRange(e.target.value)}
              style={{
                padding: '12px 16px',
                fontSize: '14px',
                fontWeight: '600',
                border: '2px solid #e5e7eb',
                borderRadius: '8px',
                background: 'white',
                cursor: 'pointer',
                outline: 'none'
              }}
            >
              <option value="7days">Last 7 Days</option>
              <option value="30days">Last 30 Days</option>
              <option value="90days">Last 90 Days</option>
              <option value="year">Last Year</option>
              <option value="all">All Time</option>
            </select>
          </div>
        </div>

        {/* Key Metrics Cards */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: window.innerWidth <= 768 ? '1fr' : 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: '16px',
          marginBottom: '32px'
        }}>
          {/* Total Revenue */}
          <div style={{
            background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
            padding: '24px',
            borderRadius: '16px',
            color: 'white',
            boxShadow: '0 10px 30px rgba(59, 130, 246, 0.3)'
          }}>
            <div style={{ fontSize: '14px', opacity: 0.9, marginBottom: '8px', fontWeight: '600' }}>
              TOTAL REVENUE
            </div>
            <div style={{ fontSize: '36px', fontWeight: '700', marginBottom: '4px' }}>
              ${salesData?.totalRevenue?.toFixed(0) || 0}
            </div>
            <div style={{ fontSize: '13px', opacity: 0.8 }}>
              {salesData?.totalOrders || 0} orders
            </div>
          </div>

          {/* Total Products */}
          <div style={{
            background: 'white',
            padding: '24px',
            borderRadius: '16px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
          }}>
            <div style={{ fontSize: '14px', color: '#6b7280', marginBottom: '8px', fontWeight: '600' }}>
              ACTIVE PRODUCTS
            </div>
            <div style={{ fontSize: '36px', fontWeight: '700', color: '#1f2937', marginBottom: '4px' }}>
              {performanceData?.summary?.totalProducts || 0}
            </div>
            <div style={{ fontSize: '13px', color: '#10b981' }}>
              {inventoryReport?.summary?.inStock || 0} in stock
            </div>
          </div>

          {/* Stock Value */}
          <div style={{
            background: 'white',
            padding: '24px',
            borderRadius: '16px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
          }}>
            <div style={{ fontSize: '14px', color: '#6b7280', marginBottom: '8px', fontWeight: '600' }}>
              STOCK VALUE
            </div>
            <div style={{ fontSize: '36px', fontWeight: '700', color: '#1f2937', marginBottom: '4px' }}>
              ${performanceData?.summary?.totalStockValue?.toFixed(0) || 0}
            </div>
            <div style={{ fontSize: '13px', color: '#f59e0b' }}>
              {inventoryReport?.summary?.lowStock || 0} low stock items
            </div>
          </div>

          {/* Avg Velocity */}
          <div style={{
            background: 'white',
            padding: '24px',
            borderRadius: '16px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
          }}>
            <div style={{ fontSize: '14px', color: '#6b7280', marginBottom: '8px', fontWeight: '600' }}>
              AVG SALES VELOCITY
            </div>
            <div style={{ fontSize: '36px', fontWeight: '700', color: '#1f2937', marginBottom: '4px' }}>
              {performanceData?.summary?.avgSalesVelocity || '0.00'}
            </div>
            <div style={{ fontSize: '13px', color: '#6b7280' }}>
              units per day
            </div>
          </div>
        </div>

        {/* Charts Row 1 */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: window.innerWidth <= 768 ? '1fr' : 'repeat(2, 1fr)',
          gap: '24px',
          marginBottom: '32px'
        }}>
          {/* Top Selling Products */}
          <div style={{
            background: 'white',
            padding: '24px',
            borderRadius: '16px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
          }}>
            <h3 style={{ marginBottom: '20px', fontSize: '18px', fontWeight: '700' }}>
              🏆 Top Selling Products
            </h3>
            <div style={{ height: '300px' }}>
              {topProductsChartData() && (
                <Bar data={topProductsChartData()} options={chartOptions} />
              )}
            </div>
          </div>

          {/* Category Revenue */}
          <div style={{
            background: 'white',
            padding: '24px',
            borderRadius: '16px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
          }}>
            <h3 style={{ marginBottom: '20px', fontSize: '18px', fontWeight: '700' }}>
              📦 Revenue by Category
            </h3>
            <div style={{ height: '300px', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
              {categoryRevenueChartData() && (
                <Doughnut data={categoryRevenueChartData()} options={{
                  ...chartOptions,
                  scales: undefined
                }} />
              )}
            </div>
          </div>
        </div>

        {/* Charts Row 2 */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: window.innerWidth <= 768 ? '1fr' : 'repeat(2, 1fr)',
          gap: '24px',
          marginBottom: '32px'
        }}>
          {/* Sales Velocity */}
          <div style={{
            background: 'white',
            padding: '24px',
            borderRadius: '16px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
          }}>
            <h3 style={{ marginBottom: '20px', fontSize: '18px', fontWeight: '700' }}>
              ⚡ Sales Velocity (Top Products)
            </h3>
            <div style={{ height: '300px' }}>
              {velocityChartData() && (
                <Bar data={velocityChartData()} options={chartOptions} />
              )}
            </div>
          </div>

          {/* Stock Status */}
          <div style={{
            background: 'white',
            padding: '24px',
            borderRadius: '16px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
          }}>
            <h3 style={{ marginBottom: '20px', fontSize: '18px', fontWeight: '700' }}>
              📊 Inventory Status
            </h3>
            <div style={{ 
              display: 'flex',
              flexDirection: 'column',
              gap: '16px',
              height: '300px',
              justifyContent: 'center'
            }}>
              {/* In Stock */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <div style={{ 
                    width: '12px', 
                    height: '12px', 
                    background: '#10b981', 
                    borderRadius: '50%' 
                  }} />
                  <span style={{ fontSize: '16px', fontWeight: '600' }}>In Stock</span>
                </div>
                <span style={{ fontSize: '28px', fontWeight: '700', color: '#10b981' }}>
                  {inventoryReport?.summary?.inStock || 0}
                </span>
              </div>

              {/* Low Stock */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <div style={{ 
                    width: '12px', 
                    height: '12px', 
                    background: '#f59e0b', 
                    borderRadius: '50%' 
                  }} />
                  <span style={{ fontSize: '16px', fontWeight: '600' }}>Low Stock</span>
                </div>
                <span style={{ fontSize: '28px', fontWeight: '700', color: '#f59e0b' }}>
                  {inventoryReport?.summary?.lowStock || 0}
                </span>
              </div>

              {/* Out of Stock */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <div style={{ 
                    width: '12px', 
                    height: '12px', 
                    background: '#ef4444', 
                    borderRadius: '50%' 
                  }} />
                  <span style={{ fontSize: '16px', fontWeight: '600' }}>Out of Stock</span>
                </div>
                <span style={{ fontSize: '28px', fontWeight: '700', color: '#ef4444' }}>
                  {inventoryReport?.summary?.outOfStock || 0}
                </span>
              </div>

              {/* Inactive */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <div style={{ 
                    width: '12px', 
                    height: '12px', 
                    background: '#9ca3af', 
                    borderRadius: '50%' 
                  }} />
                  <span style={{ fontSize: '16px', fontWeight: '600' }}>Inactive</span>
                </div>
                <span style={{ fontSize: '28px', fontWeight: '700', color: '#9ca3af' }}>
                  {inventoryReport?.summary?.inactiveProducts || 0}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Product Performance Table */}
        <div style={{
          background: 'white',
          padding: '24px',
          borderRadius: '16px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
        }}>
          <h3 style={{ marginBottom: '20px', fontSize: '18px', fontWeight: '700' }}>
            📈 Product Performance Details
          </h3>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: '#f9fafb', borderBottom: '2px solid #e5e7eb' }}>
                  <th style={{ padding: '12px', textAlign: 'left', fontSize: '12px', fontWeight: '700', color: '#6b7280' }}>
                    PRODUCT
                  </th>
                  <th style={{ padding: '12px', textAlign: 'right', fontSize: '12px', fontWeight: '700', color: '#6b7280' }}>
                    REVENUE
                  </th>
                  <th style={{ padding: '12px', textAlign: 'right', fontSize: '12px', fontWeight: '700', color: '#6b7280' }}>
                    SOLD
                  </th>
                  <th style={{ padding: '12px', textAlign: 'right', fontSize: '12px', fontWeight: '700', color: '#6b7280' }}>
                    STOCK
                  </th>
                  <th style={{ padding: '12px', textAlign: 'right', fontSize: '12px', fontWeight: '700', color: '#6b7280' }}>
                    VELOCITY
                  </th>
                  <th style={{ padding: '12px', textAlign: 'right', fontSize: '12px', fontWeight: '700', color: '#6b7280' }}>
                    TURNOVER
                  </th>
                  <th style={{ padding: '12px', textAlign: 'right', fontSize: '12px', fontWeight: '700', color: '#6b7280' }}>
                    DAYS LEFT
                  </th>
                </tr>
              </thead>
              <tbody>
                {performanceData?.performance?.slice(0, 20).map((product, index) => (
                  <tr key={index} style={{ borderBottom: '1px solid #e5e7eb' }}>
                    <td style={{ padding: '12px' }}>
                      <div style={{ fontWeight: '600', color: '#1f2937' }}>{product.name}</div>
                      <div style={{ fontSize: '12px', color: '#6b7280' }}>
                        {Array.isArray(product.category) ? product.category.join(', ') : product.category}
                      </div>
                    </td>
                    <td style={{ padding: '12px', textAlign: 'right', fontWeight: '600', color: '#10b981' }}>
                      ${product.periodRevenue.toFixed(2)}
                    </td>
                    <td style={{ padding: '12px', textAlign: 'right' }}>
                      {product.periodSales}
                    </td>
                    <td style={{ padding: '12px', textAlign: 'right', color: product.currentStock === 0 ? '#ef4444' : '#1f2937' }}>
                      {product.currentStock}
                    </td>
                    <td style={{ padding: '12px', textAlign: 'right' }}>
                      {product.salesVelocity}/day
                    </td>
                    <td style={{ padding: '12px', textAlign: 'right' }}>
                      {product.turnoverRate}x
                    </td>
                    <td style={{ padding: '12px', textAlign: 'right', color: product.daysOfStockRemaining < 7 ? '#ef4444' : '#1f2937' }}>
                      {product.daysOfStockRemaining === 999 ? '∞' : `${product.daysOfStockRemaining}d`}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </SidebarLayout>
  );
};

export default ProductAnalytics;
